/**
 * Adapter for the Mauvely design system bundle.
 *
 * `_ds_bundle.js` is the design handoff's prebuilt IIFE (handoff 227a0c14,
 * newer than the 26bf2d40 the other web repos carry). It reads a bare `React`
 * global and assigns its components onto `window.MauvelyDesignSystem_227a0c`.
 * The types below are read off the bundle's own function signatures, not
 * guessed — see the prop lists in `web/public/_ds/mauvely/_ds_bundle.js`.
 *
 * Two things this handoff differs on from the older one:
 *   · icons are Phosphor, and the Icon component injects a stylesheet from
 *     unpkg unless one with its id is already on the page. index.html links the
 *     self-hosted copies under those ids, so it never does.
 *   · Button has the app-chrome variants the prototype uses: `accent` (an
 *     outline over a violet wash) and `neutral`, and `density="app"`.
 */

import React from 'react';
import * as ReactDOM from 'react-dom';

const BUNDLE_URL = '/_ds/mauvely/_ds_bundle.js';

type C<P = Record<string, unknown>> = React.ComponentType<P>;

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'neutral' | 'soft' | 'signal' | 'danger';

export interface DesignSystem {
  Button: C<{
    variant?: ButtonVariant;
    size?: 'sm' | 'md' | 'lg';
    /** `app` is the tighter chrome geometry the prototype uses throughout. */
    density?: 'marketing' | 'app';
    /** Phosphor glyph names, without the `ph-` prefix. */
    iconLeft?: string;
    iconRight?: string;
    fullWidth?: boolean;
    disabled?: boolean;
    href?: string;
    type?: 'button' | 'submit';
    title?: string;
    /** Passed through to the element, as the bundle spreads the rest. */
    autoFocus?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  IconButton: C<{
    icon: string;
    label: string;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'primary' | 'ghost';
    active?: boolean;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    style?: React.CSSProperties;
  }>;
  Icon: C<{
    name: string;
    size?: number;
    weight?: 'regular' | 'fill';
    color?: string;
    style?: React.CSSProperties;
  }>;
  Wordmark: C<{
    lockup?: 'primary' | 'reversed' | 'solid' | 'ink';
    word?: string;
    size?: number;
    showDot?: boolean;
    /** A Phosphor glyph for the BrandBadge tile to the left. */
    badge?: string;
    tagline?: string;
    style?: React.CSSProperties;
  }>;
  BrandBadge: C<{ glyph?: string; box?: number; style?: React.CSSProperties }>;
  Switch: C<{
    label?: string;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    size?: 'sm' | 'md';
    disabled?: boolean;
    /** `(nextValue, event)` — not a change event. */
    onChange?: (checked: boolean, e: React.SyntheticEvent) => void;
    style?: React.CSSProperties;
  }>;
  Checkbox: C<{
    label?: string;
    description?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    onChange?: (checked: boolean, e: React.SyntheticEvent) => void;
    style?: React.CSSProperties;
  }>;
  Input: C<{
    label?: string;
    hint?: string;
    error?: string;
    icon?: string;
    size?: 'sm' | 'md' | 'lg';
    multiline?: boolean;
    rows?: number;
    disabled?: boolean;
    id?: string;
    type?: string;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    autoComplete?: string;
    autoFocus?: boolean;
    onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    style?: React.CSSProperties;
  }>;
  Select: C<{
    label?: string;
    hint?: string;
    options?: { value: string; label: string }[];
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    style?: React.CSSProperties;
  }>;
  Dialog: C<{
    open?: boolean;
    title?: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    onClose?: () => void;
    width?: number;
    position?: 'absolute' | 'fixed';
    style?: React.CSSProperties;
  }>;
  Progress: C<{
    value?: number;
    label?: string;
    showValue?: boolean;
    size?: 'sm' | 'md' | 'lg';
    tone?: 'signal' | 'primary' | 'accent';
    style?: React.CSSProperties;
  }>;
  Toast: C<{
    title?: React.ReactNode;
    message?: React.ReactNode;
    tone?: 'neutral' | 'success' | 'warning' | 'danger';
    action?: React.ReactNode;
    onDismiss?: () => void;
    style?: React.CSSProperties;
  }>;
  Tooltip: C<{
    content: React.ReactNode;
    side?: 'top' | 'bottom' | 'left' | 'right';
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Badge: C<{
    tone?: 'neutral' | 'primary' | 'accent' | 'signal' | 'success' | 'warning' | 'danger';
    icon?: string;
    dot?: boolean;
    mono?: boolean;
    children?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
  Tabs: C<{
    items?: (string | { value: string; label: string; icon?: string })[];
    value?: string;
    defaultValue?: string;
    onChange?: (value: string, e: React.SyntheticEvent) => void;
    variant?: 'underline' | 'pill';
    style?: React.CSSProperties;
  }>;
  /** The bundle collects per-component init failures here instead of throwing. */
  __errors?: unknown[];
}

declare global {
  interface Window {
    React?: typeof React;
    ReactDOM?: typeof ReactDOM;
    MauvelyDesignSystem_227a0c?: DesignSystem;
  }
}

let loaded: Promise<DesignSystem> | null = null;

export function loadDesignSystem(): Promise<DesignSystem> {
  if (loaded) return loaded;

  loaded = new Promise<DesignSystem>((resolve, reject) => {
    // The bundle reads these at evaluation time. The very same React instance
    // the app imports, so hooks work across the boundary.
    window.React = React;
    window.ReactDOM = ReactDOM;

    const script = document.createElement('script');
    script.src = BUNDLE_URL;
    script.async = false;

    script.onload = () => {
      const ns = window.MauvelyDesignSystem_227a0c;
      if (!ns) {
        reject(new Error('Design system bundle loaded but did not register its namespace.'));
        return;
      }
      if (ns.__errors?.length) {
        console.error('Design system components failed to initialise:', ns.__errors);
      }
      resolve(ns);
    };

    script.onerror = () => reject(new Error(`Could not load ${BUNDLE_URL}`));
    document.head.appendChild(script);
  });

  return loaded;
}

/** Synchronous accessor for render code. Safe because the app does not mount
 *  until `loadDesignSystem()` resolves — see main.tsx. */
export function ds(): DesignSystem {
  const ns = window.MauvelyDesignSystem_227a0c;
  if (!ns) throw new Error('Design system used before it finished loading.');
  return ns;
}
